<template>
  <div class="fm-page-shell">
    <!-- ===== CONTEXTUAL RAIL ===== -->
    <aside class="fm-rail">
      <div class="fm-rail-label">Resources</div>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': typeFilter === 'all' }" @click="typeFilter = 'all'">
        <span class="fm-view-dot" style="background:#94A3B8"></span>All
        <span class="fm-view-count">{{ viewCounts.all }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': typeFilter === 'database' }" @click="typeFilter = 'database'">
        <span class="fm-view-dot" style="background:#10B981"></span>Database
        <span class="fm-view-count">{{ viewCounts.database }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': typeFilter === 'cluster' }" @click="typeFilter = 'cluster'">
        <span class="fm-view-dot" style="background:#3B82F6"></span>Cluster
        <span class="fm-view-count">{{ viewCounts.cluster }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': typeFilter === 'server' }" @click="typeFilter = 'server'">
        <span class="fm-view-dot" style="background:#F59E0B"></span>Server
        <span class="fm-view-count">{{ viewCounts.server }}</span>
      </button>

      <template v-if="subTypeFacets.length">
        <div class="fm-rail-sublabel">Subtype</div>
        <button type="button" class="fm-view" :class="{ 'fm-view--active': subTypeFilter === 'all' }" @click="subTypeFilter = 'all'">
          <span class="fm-view-dot" style="background:#94A3B8"></span>All subtypes
          <span class="fm-view-count">{{ typeScopedResources.length }}</span>
        </button>
        <button v-for="s in subTypeFacets" :key="s.key" type="button" class="fm-view"
                :class="{ 'fm-view--active': subTypeFilter === s.key }" @click="subTypeFilter = s.key">
          <span class="fm-view-dot" style="background:#8B5CF6"></span>{{ s.key }}
          <span class="fm-view-count">{{ s.count }}</span>
        </button>
      </template>
    </aside>

    <!-- ===== MAIN ===== -->
    <main class="fm-main">
      <b-overlay :show="loading" spinner small>
        <div class="fm-page">
          <div class="fm-page-head">
            <div class="fm-page-title">Roles</div>
            <button class="fm-btn fm-btn--primary" @click="showAddResourceModal"><i class="fa-solid fa-plus"></i>New Role</button>
          </div>
          <div class="fm-page-desc">Roles and resource permissions for databases, clusters and servers.</div>

          <div class="fm-toolbar">
            <div class="fm-search">
              <i class="fa-solid fa-magnifying-glass"></i>
              <b-form-input type="search" v-model="searchQuery" placeholder="Search roles by name..."></b-form-input>
            </div>
            <div v-if="scopedResources.length > perPage" class="fm-toolbar-pager">
              <b-pagination
                v-model="currentPage"
                :total-rows="scopedResources.length"
                :per-page="perPage"
                :disabled="loading"
                size="sm"
              ></b-pagination>
            </div>
          </div>

          <div v-if="visibleResources.length > 0">
            <div class="fm-list">
              <div v-for="resource in visibleResources" :key="resource.ResourceId" class="fm-row" @click="openRow(resource)">
                <div class="fm-row-main">
                  <div class="fm-row-top">
                    <span class="fm-row-name">{{ resource.Name }}</span>
                    <span v-if="resource.ResourceType" class="fm-tag">{{ resource.ResourceType }}</span>
                    <span v-if="resource.ResourceSubType" class="fm-tag">{{ resource.ResourceSubType }}</span>
                  </div>
                  <div v-if="resource.Description" class="fm-row-desc">{{ resource.Description }}</div>
                </div>
                <button type="button" class="fm-row-delete" title="Delete role" @click.stop="showDeleteModal(resource.ResourceId)">
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
          <div v-else-if="resources.length > 0 && visibleResources.length === 0" class="fm-empty">
            No roles match this filter.
          </div>
          <div v-else class="fm-empty">
            No Roles added yet.
            <div class="mt-3"><button class="fm-btn fm-btn--primary" @click="showAddResourceModal"><i class="fa-solid fa-plus"></i>Add New</button></div>
          </div>
        </div>

        <!-- Add Resource Modal -->
        <b-modal v-model="showModal" title="Add Role" hide-footer>
          <b-form @submit.stop.prevent="addResource">
            <b-form-group label="Name" label-for="name">
              <b-form-input id="name" v-model="newResource.name" required></b-form-input>
            </b-form-group>
            <b-form-group label="Description" label-for="description">
              <b-form-input id="description" v-model="newResource.description"></b-form-input>
            </b-form-group>
            <b-row class="mb-3">
              <b-col cols="4">
                <b-form-group label="Type" label-for="type">
                  <b-form-select id="type" v-model="newResource.type" :options="resourceTypes" required
                                 @change="clearSubtype"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col cols="8">
                <b-form-group label="Subtype" label-for="subtype">
                  <b-form-select id="subtype" v-model="newResource.subtype" :options="getSubtypes(newResource.type)"
                                 required></b-form-select>
                </b-form-group>
              </b-col>
            </b-row>

            <component :is="formComponent" :resource="newResource" :component="formComponent"></component>

            <div class="fm-modal-foot">
              <button type="submit" class="fm-btn fm-btn--primary">Add Role</button>
            </div>
          </b-form>
        </b-modal>
        <!-- Delete Resource Modal -->
        <b-modal v-model="deleteModal" title="Delete Role" hide-footer>
          <b-form @submit.stop.prevent="confirmDeleteResource">
            <p>Are you sure you want to delete this role? This can't be undone.</p>
            <div class="fm-modal-foot">
              <button type="button" class="fm-btn fm-btn--ghost" @click="deleteModal = false">Cancel</button>
              <button type="submit" class="fm-btn fm-btn--danger">Delete Role</button>
            </div>
          </b-form>
        </b-modal>
      </b-overlay>
    </main>

    <!-- ===== DETAIL DRAWER ===== -->
    <detail-drawer
      :open="drawerOpen"
      kind="Role"
      :title="selected.Name || ''"
      :fields="drawerFields"
      @close="closeDrawer">
      <template #footer>
        <button class="fm-btn fm-btn--primary" @click="drawerAction('edit')">
          <i class="fa-solid fa-pen"></i>Edit Role
        </button>
        <button class="fm-btn fm-btn--danger" @click="drawerAction('delete')">
          <i class="fa-solid fa-trash"></i>Delete
        </button>
      </template>
    </detail-drawer>
  </div>
</template>

<script>
import apiClient from '@/http';
import DetailDrawer from '@/components/DetailDrawer.vue';
import K8sForm from "@/pages/forms/K8sForm.vue";
import DatabaseForm from "@/pages/forms/DatabaseForm.vue";


import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BOverlay,
  BFormSelect,
  BPagination,
} from 'bootstrap-vue';
import SSHForm from "@/pages/forms/SSHForm.vue";

export default {
  components: {
    DetailDrawer,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BOverlay,
    BFormSelect,
    DatabaseForm,
    K8sForm,
    SSHForm,
  },
  data() {
    return {
      loading: false,
      showModal: false,
      deleteModal: false,
      searchQuery: '',
      typeFilter: 'all',
      subTypeFilter: 'all',
      drawerOpen: false,
      selected: {},

      agents: [], // List of agents fetched from the backend
      selectedAgent: null, // Selected agent for testing the connection
      resources: [],
      currentPage: 1,
      perPage: 10,
      resourceToDelete: '',
      newResource: {
        name: '',
        description: '',
        type: 'database',
        subtype: '',
        k8sClusterType: '',
        authMethod: '',
        region: '',
        clusterName: '',
        username: '',
        password: '',
        database: '',
        k8sRole: '',
        keyFile: ''
      },
      resourceTypes: [
        {value: 'database', text: 'Database'},
        {value: 'cluster', text: 'Cluster'},
        {value: 'server', text: 'Server'},
        // Add more types as needed
      ],
      k8sClusterTypes: [
        {value: 'aws_eks', text: 'AWS EKS'},
        {value: 'any', text: 'Others'},
      ],
      authMethods: [
        {value: 'instance-profile', text: 'Instance Profile'},
        {value: 'kubeconfig', text: 'Provide Kubeconfig'},
      ],
    };
  },
  methods: {
    openRow(resource) {
      this.selected = resource;
      this.drawerOpen = true;
    },
    closeDrawer() {
      this.drawerOpen = false;
    },
    drawerAction(type) {
      const res = this.selected;
      this.closeDrawer();
      if (type === 'edit') {
        this.showAddResourceModal();
      } else if (type === 'delete') {
        this.showDeleteModal(res.ResourceId);
      }
    },
    showAddResourceModal() {
      this.showModal = true;
    },
    async addResource() {
      this.loading = true;
      try {
        let payload = {};

        // Add database-specific fields
        if (this.newResource.type === 'database') {
          payload = {
            Name: this.newResource.name,
            Description: this.newResource.description,
            Username: this.newResource.username,
            Password: this.newResource.password,
            Database: this.newResource.database,
          };
        }

        // Add Kubernetes role if it's selected
        if (this.newResource.type === 'cluster' && this.newResource.subtype === 'k8s') {
          payload = {
            Name: this.newResource.name,
            Description: this.newResource.description,
            K8sClusterType: this.newResource.k8sClusterType,
            AuthMethod: this.newResource.authMethod,
            Region: this.newResource.region,
            ClusterName: this.newResource.clusterName,
            K8sRole: this.newResource.k8sRole,
          };
        }
        if (this.newResource.type === 'server' && this.newResource.subtype === 'ssh') {
          payload = {
            Name: this.newResource.name,
            Description: this.newResource.description,
            Username: this.newResource.username,
            AuthMethod: this.newResource.authMethod,
          };

          // Add either password or key file based on auth method
          if (this.newResource.authMethod === 'password') {
            payload.Password = this.newResource.password;
          } else if (this.newResource.authMethod === 'key') {
            payload.KeyFile = this.newResource.keyFile;
          }
        }

        await apiClient.post(`/resources/${this.newResource.type}/${this.newResource.subtype}`, payload);
        await this.fetchResources();
        this.showNotification('Resource added successfully', 'success');
        this.showModal = false;
      } catch (error) {
        this.showNotification('Failed to add resource', 'danger');
      } finally {
        this.loading = false;
      }
    },
    showDeleteModal(resourceId) {
      this.resourceToDelete = resourceId;
      this.deleteModal = true;
    },

    getSubtypes(type) {
      // Return appropriate subtypes based on the type
      if (type === 'database') {
        return [
          {value: 'postgres', text: 'PostgreSQL'},
          {value: 'mysql', text: 'MySql'},
        ];
      } else if (type === 'cluster') {
        return [
          {value: 'k8s', text: 'Kubernetes'},
          // Add more subtypes as needed
        ];
      } else if (type === 'server') {
        return [
          {value: 'ssh', text: 'SSH'},
        ];
      }
      return [];
    },
    async fetchResources() {
      this.loading = true;
      try {
        // Load every role so filtering, counts and pagination are correct
        // client-side. The backend's paginated total_count is unreliable at
        // non-zero offsets, so we page through it here and count locally.
        const pageSize = 200;
        let page = 1;
        let all = [];
        while (true) {
          const response = await apiClient.get(`/resources?page=${page}&pageSize=${pageSize}`);
          const batch = (response.data && response.data.resources) ? response.data.resources : [];
          all = all.concat(batch);
          if (batch.length < pageSize) break;
          page += 1;
        }
        this.resources = all;

        const totalPages = Math.max(1, Math.ceil(this.scopedResources.length / this.perPage));
        if (this.currentPage > totalPages) this.currentPage = totalPages;
      } catch (error) {
        this.showNotification('Failed to fetch resources', 'danger');
      } finally {
        this.loading = false;
      }
    },

    async confirmDeleteResource() {
      let toDelete = this.resourceToDelete;
      this.resourceToDelete = '';
      this.loading = true;
      try {
        const response = await apiClient.delete(`/resources/${toDelete}`);
        await this.fetchResources();
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error deleting resource';
        this.showNotification(errorMessage, 'danger');
      } finally {
        this.deleteModal = false;
        this.loading = false;
      }

    },
    clearSubtype() {
      this.newResource.subtype = '';
    },
    showNotification(message, type) {
      this.$bvToast.toast(message, {
        title: `Info`,
        variant: type,
        solid: true,
      });
    },
  },
  mounted() {
    // Initialize page to 1 and fetch resources
    this.currentPage = 1;
    this.fetchResources();
  },
  watch: {
    searchQuery(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentPage = 1;
      }
    },
    typeFilter() {
      this.subTypeFilter = 'all';
      this.currentPage = 1;
    },
    subTypeFilter() {
      this.currentPage = 1;
    }
  },
  computed: {
    filteredResources() {
      if (!this.searchQuery) {
        return this.resources;
      }

      const query = this.searchQuery.toLowerCase();
      return this.resources.filter(resource =>
        resource.Name.toLowerCase().includes(query) ||
        (resource.Description && resource.Description.toLowerCase().includes(query)) ||
        resource.ResourceType.toLowerCase().includes(query) ||
        resource.ResourceSubType.toLowerCase().includes(query)
      );
    },
    typeScopedResources() {
      if (this.typeFilter === 'all') {
        return this.filteredResources;
      }
      return this.filteredResources.filter(r => r.ResourceType === this.typeFilter);
    },
    subTypeFacets() {
      const counts = {};
      this.typeScopedResources.forEach(r => {
        const key = r.ResourceSubType;
        if (key) counts[key] = (counts[key] || 0) + 1;
      });
      return Object.keys(counts).sort().map(key => ({ key, count: counts[key] }));
    },
    scopedResources() {
      if (this.subTypeFilter === 'all') {
        return this.typeScopedResources;
      }
      return this.typeScopedResources.filter(r => r.ResourceSubType === this.subTypeFilter);
    },
    visibleResources() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.scopedResources.slice(start, start + this.perPage);
    },
    viewCounts() {
      return {
        all: this.filteredResources.length,
        database: this.filteredResources.filter(r => r.ResourceType === 'database').length,
        cluster: this.filteredResources.filter(r => r.ResourceType === 'cluster').length,
        server: this.filteredResources.filter(r => r.ResourceType === 'server').length,
      };
    },
    formComponent() {
      if (this.newResource.type === 'database') {
        return 'DatabaseForm';
      } else if (this.newResource.type === 'cluster') {
        return 'K8sForm';
      } else if (this.newResource.type === 'server' && this.newResource.subtype === 'ssh') {
        return 'SSHForm';
      }
      return null;
    },
    drawerFields() {
      const r = this.selected;
      if (!r || !r.Name) return [];
      return [
        {label: 'Description', value: r.Description || 'No description'},
        {label: 'Type', value: r.ResourceType},
        {label: 'Subtype', value: r.ResourceSubType},
      ];
    },
  },
};
</script>

<style scoped>
.fm-row-delete {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: var(--fm-r-control);
  background: transparent;
  color: var(--fm-slate-400);
  cursor: pointer;
  transition: background-color .12s var(--fm-ease), color .12s var(--fm-ease), border-color .12s var(--fm-ease);
}

.fm-row-delete:hover {
  background: var(--fm-danger-soft, #FEF2F2);
  border-color: var(--fm-danger, #EF4444);
  color: var(--fm-danger, #EF4444);
}
</style>
